import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchConflictedAppointments,
  resolveAppointmentConflict,
} from "../../../store/thunks/appointmentsThunk";
import { toast } from "react-toastify";
import {
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";

export default function ConflictManagement({ onAppointmentClick }) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [resolvingConflict, setResolvingConflict] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [conflictsPerPage] = useState(5);

  // Accordion state
  const [expandedConflicts, setExpandedConflicts] = useState(new Set());

  // Confirmation modal state
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  // Information modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  const conflictedAppointments = useSelector(
    (state) => state.appointments.conflictedAppointments || []
  );
  const totalConflicts = useSelector(
    (state) => state.appointments.totalConflicts || 0
  );

  useEffect(() => {
    loadConflicts();
  }, []);

  useEffect(() => {
    console.log("Conflicted appointments updated:", conflictedAppointments);
  }, [conflictedAppointments]);

  const loadConflicts = async () => {
    setIsLoading(true);
    try {
      const result = await dispatch(fetchConflictedAppointments()).unwrap();
      console.log("Loaded conflicted appointments:", result);
    } catch (error) {
      toast.error("Failed to load conflicts");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolveConflict = async (appointmentId, resolution) => {
    setResolvingConflict(appointmentId);
    try {
      await dispatch(
        resolveAppointmentConflict({
          appointmentId,
          resolution,
          resolvedBy: null,
        })
      ).unwrap();

      toast.success(`Conflict resolved: ${resolution}`);
      loadConflicts();
    } catch (error) {
      toast.error("Failed to resolve conflict");
    } finally {
      setResolvingConflict(null);
    }
  };

  const handleResolutionClick = (appointmentId, resolution) => {
    const resolutionMessages = {
      rescheduled:
        "This will mark the conflict as resolved by rescheduling. The appointment will remain in the system but the conflict flag will be removed.",
      cancelled:
        "This will mark the conflict as resolved by cancellation. The appointment will be cancelled and the conflict flag will be removed.",
      ignored:
        "This will acknowledge the conflict but keep it in the system. The conflict will be marked as resolved but the appointments will remain as-is.",
    };

    setConfirmationData({
      appointmentId,
      resolution,
      message: resolutionMessages[resolution],
    });
    setShowConfirmationModal(true);
  };

  const confirmResolution = () => {
    if (confirmationData) {
      handleResolveConflict(
        confirmationData.appointmentId,
        confirmationData.resolution
      );
      setShowConfirmationModal(false);
      setConfirmationData(null);
    }
  };

  const handleAppointmentClick = (appointment) => {
    console.log("ConflictManagement: Appointment clicked:", appointment);
    console.log(
      "ConflictManagement: onAppointmentClick prop:",
      onAppointmentClick
    );

    // Enhanced null checking for appointment data
    if (!appointment) {
      toast.error("Appointment data is missing");
      return;
    }

    // Check if appointment has required data for AppointmentSummary
    if (!appointment.client || !appointment.clinician) {
      console.warn(
        "ConflictManagement: Appointment missing client or clinician:",
        {
          hasClient: !!appointment.client,
          hasClinician: !!appointment.clinician,
          appointment,
        }
      );
      toast.error(
        "Appointment data is incomplete - missing client or clinician information"
      );
      return;
    }

    // Use the parent component's appointment click handler
    if (onAppointmentClick) {
      console.log(
        "ConflictManagement: Calling onAppointmentClick with:",
        appointment
      );
      try {
        onAppointmentClick(appointment);
        console.log(
          "ConflictManagement: onAppointmentClick called successfully"
        );
      } catch (error) {
        console.error(
          "ConflictManagement: Error calling onAppointmentClick:",
          error
        );
        toast.error("Error opening appointment details");
      }
    } else {
      console.error(
        "ConflictManagement: onAppointmentClick prop is not provided"
      );
      toast.error("Unable to open appointment details");
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const getConflictTypeLabel = (type) => {
    switch (type) {
      case "treating":
        return "Same treating clinician";
      case "supervising":
        return "Same supervising clinician";
      case "resource":
        return "Same resource";
      default:
        return type;
    }
  };

  const getResolutionButtonVariant = (resolution) => {
    switch (resolution) {
      case "rescheduled":
        return "bg-blue-600 hover:bg-blue-700 text-white";
      case "cancelled":
        return "bg-red-600 hover:bg-red-700 text-white";
      case "ignored":
        return "bg-gray-600 hover:bg-gray-700 text-white";
      default:
        return "bg-green-600 hover:bg-green-700 text-white";
    }
  };

  const getResolutionIcon = (resolution) => {
    switch (resolution) {
      case "rescheduled":
        return <ClockIcon className="h-4 w-4" />;
      case "cancelled":
        return <XCircleIcon className="h-4 w-4" />;
      case "ignored":
        return <MinusCircleIcon className="h-4 w-4" />;
      default:
        return <CheckCircleIcon className="h-4 w-4" />;
    }
  };

  const getResolutionTitle = (resolution) => {
    switch (resolution) {
      case "rescheduled":
        return "Reschedule Conflict";
      case "cancelled":
        return "Cancel Conflict";
      case "ignored":
        return "Ignore Conflict";
      default:
        return "Resolve Conflict";
    }
  };

  // Helper function to check if an appointment is cancelled
  const isAppointmentCancelled = (appointment) => {
    if (!appointment) return false;

    // Status is an array in the schema - check if any status contains "Cancelled"
    const statusArray = appointment.status;

    if (!Array.isArray(statusArray)) {
      console.warn(`[ConflictManagement] Unexpected status format for appointment ${appointment._id}:`, statusArray);
      return false;
    }

    // Check if any status in the array starts with "Cancelled"
    return statusArray.some(status => {
      if (typeof status !== 'string') return false;
      return status.startsWith('Cancelled');
    });
  };

  // Create unique conflict pairs to avoid duplicates
  const getUniqueConflicts = () => {
    const conflictPairs = new Set();
    const uniqueConflicts = [];
    let excludedCount = 0;

    conflictedAppointments.forEach((appointment) => {
      const conflicts =
        appointment.conflictDetails?.conflictingAppointments || [];
      conflicts.forEach((conflict) => {
        const conflictingAppt = conflict.appointmentId;
        if (!conflictingAppt) {
          console.warn("[ConflictManagement] Conflict missing appointmentId:", conflict);
          return;
        }

        // Exclude conflicts involving cancelled appointments
        if (isAppointmentCancelled(appointment) || isAppointmentCancelled(conflictingAppt)) {
          excludedCount++;
          console.log(`[ConflictManagement] Excluding conflict involving cancelled appointment:`, {
            appointment1Id: appointment._id,
            appointment1Status: appointment.status,
            appointment2Id: conflictingAppt._id,
            appointment2Status: conflictingAppt.status,
          });
          return;
        }

        // Create a unique key for this conflict pair
        const pairKey = [appointment._id, conflictingAppt._id].sort().join("-");

        if (!conflictPairs.has(pairKey)) {
          conflictPairs.add(pairKey);
          uniqueConflicts.push({
            appointment,
            conflictingAppt,
            conflict,
            pairKey,
          });
        }
      });
    });

    console.log(`[ConflictManagement] Conflicts found: ${uniqueConflicts.length}, Excluded (cancelled): ${excludedCount}`);
    return uniqueConflicts;
  };

  const getAppointmentTitle = (appointment) => {
    if (!appointment) return "Unknown Appointment";

    // Try to get service name first
    if (appointment.service?.name) {
      return appointment.service.name;
    }

    // Fall back to appointment title
    if (appointment.title) {
      return appointment.title;
    }

    // Last resort
    return "Untitled Appointment";
  };

  const getClientName = (appointment) => {
    if (!appointment?.client) return "Unknown Client";
    return `${appointment.client.firstName || "Unknown"} ${
      appointment.client.lastName || "Client"
    }`;
  };

  const getClinicianName = (appointment) => {
    if (!appointment?.clinician) return "Unknown Clinician";
    return `${appointment.clinician.firstName || "Unknown"} ${
      appointment.clinician.lastName || "Clinician"
    }`;
  };

  // Pagination logic
  const uniqueConflicts = getUniqueConflicts();
  const indexOfLastConflict = currentPage * conflictsPerPage;
  const indexOfFirstConflict = indexOfLastConflict - conflictsPerPage;
  const currentConflicts = uniqueConflicts.slice(
    indexOfFirstConflict,
    indexOfLastConflict
  );
  const totalPages = Math.ceil(uniqueConflicts.length / conflictsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const toggleConflictExpansion = (conflictKey) => {
    const newExpanded = new Set(expandedConflicts);
    if (newExpanded.has(conflictKey)) {
      newExpanded.delete(conflictKey);
    } else {
      newExpanded.add(conflictKey);
    }
    setExpandedConflicts(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Modern Header */}
      <div className="flex items-center justify-between p-4 bg-white rounded-sm border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-red-50 rounded-full">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Appointment Conflicts
            </h2>
            <p className="text-sm text-gray-500">
              Resolve scheduling conflicts that require attention
            </p>
          </div>
          {uniqueConflicts.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-full px-3 py-1">
              <span className="text-sm font-medium text-red-700">
                {uniqueConflicts.length} conflict
                {uniqueConflicts.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInfoModal(true)}
            className="inline-flex items-center p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-sm transition-colors"
            title="Information about conflicts">
            <InformationCircleIcon className="h-5 w-5" />
          </button>
          <button
            onClick={loadConflicts}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors">
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12 bg-white rounded-sm border border-gray-200">
          <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-blue-600 rounded-full" />
        </div>
      ) : uniqueConflicts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-sm border border-gray-200">
          <div className="flex items-center justify-center w-16 h-16 bg-green-50 rounded-full mx-auto mb-4">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No conflicts detected
          </h3>
          <p className="text-gray-500">
            All appointments are properly scheduled without conflicts
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Results Info */}
          <div className="flex items-center justify-between px-4">
            <p className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-medium">
                {indexOfFirstConflict + 1}–
                {Math.min(indexOfLastConflict, uniqueConflicts.length)}
              </span>{" "}
              of <span className="font-medium">{uniqueConflicts.length}</span>{" "}
              conflicts
            </p>
            <p className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </p>
          </div>

          {/* Conflicts List - Accordion Style */}
          <div className="space-y-2">
            {currentConflicts.map(
              ({ appointment, conflictingAppt, conflict }) => {
                const conflictKey = `${appointment._id}-${conflictingAppt._id}`;
                const isExpanded = expandedConflicts.has(conflictKey);

                return (
                  <div
                    key={conflictKey}
                    className="bg-white rounded-sm border border-gray-200 shadow-sm overflow-hidden">
                    {/* Compact Header - Always Visible */}
                    <div
                      className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleConflictExpansion(conflictKey)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-6 h-6 bg-red-50 rounded-full">
                            <ExclamationTriangleIcon className="h-3 w-3 text-red-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-4 text-sm">
                              <span className="font-medium text-red-800">
                                {getConflictTypeLabel(conflict.conflictType)}
                              </span>
                              <span className="text-gray-600">
                                {formatDate(appointment.start)} •{" "}
                                {formatTime(appointment.start)} -{" "}
                                {formatTime(appointment.end)}
                              </span>
                              <span className="text-gray-600 truncate">
                                {getClinicianName(appointment)}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {getClientName(appointment)} vs{" "}
                              {getClientName(conflictingAppt)}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {/* Quick Actions */}
                          {/* <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResolutionClick(appointment._id, "rescheduled");
                            }}
                            disabled={resolvingConflict === appointment._id}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors disabled:opacity-50"
                            title="Reschedule">
                            <ClockIcon className="h-3 w-3" />
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResolutionClick(appointment._id, "cancelled");
                            }}
                            disabled={resolvingConflict === appointment._id}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors disabled:opacity-50"
                            title="Cancel">
                            <XCircleIcon className="h-3 w-3" />
                          </button> */}

                          {/* Expand/Collapse Icon */}
                          <div className="ml-2">
                            {isExpanded ? (
                              <ChevronLeftIcon className="h-4 w-4 text-gray-400 rotate-[-90deg] transition-transform" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4 text-gray-400 transition-transform" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details - Shows on Click */}
                    {isExpanded && (
                      <div className="border-t border-gray-100">
                        <div className="p-4 bg-gray-50">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {[appointment, conflictingAppt].map(
                              (appt, index) => (
                                <div
                                  key={appt._id}
                                  onClick={() => handleAppointmentClick(appt)}
                                  className="group relative border border-gray-200 rounded p-3 bg-white hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all duration-200">
                                  {/* Appointment Badge */}
                                  <div className="absolute top-2 right-2">
                                    <span
                                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        index === 0
                                          ? "bg-blue-50 text-blue-700 border border-blue-200"
                                          : "bg-purple-50 text-purple-700 border border-purple-200"
                                      }`}>
                                      #{index + 1}
                                    </span>
                                  </div>

                                  {/* Appointment Title */}
                                  <div className="mb-2 pr-12">
                                    <h4 className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                                      {getAppointmentTitle(appt)}
                                    </h4>
                                  </div>

                                  {/* Appointment Details - Compact */}
                                  <div className="grid grid-cols-3 gap-2 text-xs">
                                    <div className="flex items-center gap-1">
                                      <UserIcon className="h-3 w-3 text-blue-600" />
                                      <div>
                                        <div className="text-gray-500">Client</div>
                                        <div className="font-medium text-gray-900">
                                          {getClientName(appt)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <UserIcon className="h-3 w-3 text-green-600" />
                                      <div>
                                        <div className="text-gray-500">Clinician</div>
                                        <div className="font-medium text-gray-900">
                                          {getClinicianName(appt)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <ClockIcon className="h-3 w-3 text-gray-600" />
                                      <div>
                                        <div className="text-gray-500">Time</div>
                                        <div className="font-medium text-gray-900">
                                          {formatTime(appt.start)} - {formatTime(appt.end)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>

                          {/* Additional Actions in Expanded View */}
                          {/* <div className="mt-3 pt-3 border-t border-gray-200 flex gap-2">
                            <button
                              onClick={() => handleResolutionClick(appointment._id, "rescheduled")}
                              disabled={resolvingConflict === appointment._id}
                              className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors disabled:opacity-50">
                              <ClockIcon className="h-3 w-3 mr-1" />
                              Reschedule
                            </button>
                            <button
                              onClick={() => handleResolutionClick(appointment._id, "cancelled")}
                              disabled={resolvingConflict === appointment._id}
                              className="inline-flex items-center px-3 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors disabled:opacity-50">
                              <XCircleIcon className="h-3 w-3 mr-1" />
                              Cancel
                            </button>
                            <button
                              onClick={() => handleResolutionClick(appointment._id, "ignored")}
                              disabled={resolvingConflict === appointment._id}
                              className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition-colors disabled:opacity-50">
                              <MinusCircleIcon className="h-3 w-3 mr-1" />
                              Ignore
                            </button>
                          </div> */}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
            )}
          </div>

          {/* Modern Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-sm">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => paginate(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
                        currentPage === page
                          ? "bg-blue-600 text-white shadow-sm"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}>
                      {page}
                    </button>
                  )
                )}
              </div>

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                Next
                <ChevronRightIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Information Modal */}
      <Transition.Root show={showInfoModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setShowInfoModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="relative transform overflow-hidden rounded-sm bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-6">
                  {/* Top-right X button */}
                  <div className="absolute top-4 right-4">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      onClick={() => setShowInfoModal(false)}>
                      <span className="sr-only">Close</span>
                      <svg
                        className="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="sm:flex sm:items-start">
                    <div className="text-center sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900">
                        Appointment Conflicts Information
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Some appointments are in conflict because the same
                          clinician has been scheduled with more than one client
                          at the same time.
                        </p>

                        <p className="mt-3 text-sm text-gray-500">
                          To remove these conflicts, you must reschedule one of
                          the appointments, change the treating clinician, or
                          reassign the client to a different time slot.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Removed bottom button group */}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
